import {
  collection, doc, getDoc, getDocs, getDocsFromServer, onSnapshot, query, runTransaction,
  serverTimestamp, where,
} from 'firebase/firestore';
import db, { auth } from '../firebaseConfig';
import { getAuthorizedUserRole } from './authConfig';
import { getCollectionName, VALID_COUNTRY_IDS } from './countryConfig';
import { getProductVariants } from './productVariants';

export const MAX_SAMPLE_BATCH = 10;
export const MAX_SAMPLE_NUMBER_LENGTH = 100;
export const SAMPLE_COUNTER_PATH = 'sampleCounters/global';

const fail = (code, message, extra = {}) => Object.assign(new Error(message), { code, ...extra });
const validCountry = (country) => {
  if (!VALID_COUNTRY_IDS.includes(country)) throw fail('invalid-country', '商品國家無效');
};
const validId = (value, name) => {
  if (typeof value !== 'string' || !value || value.includes('/')) throw fail('invalid-id', `${name}無效`);
};
export const validateSampleNumber = (number) => {
  if (typeof number !== 'string' || number.length > MAX_SAMPLE_NUMBER_LENGTH
      || [...number].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
      || (number && !number.trim())) {
    throw fail('invalid-number', '樣品編號須為 1–100 個可見文字字元，或留空；前導零及大小寫會完整保留');
  }
  return number;
};
const validateLocation = (location) => {
  if (typeof location !== 'string' || location.length > 200) throw fail('invalid-location', '位置最多 200 個字元');
  return location;
};

// Encode UTF-16 code units without normalization, truncation, or path separators.
// This is injective even for non-ASCII input; "001", "1", "a", and "A" stay distinct.
export const sampleNumberClaimId = (number) => {
  validateSampleNumber(number);
  if (!number) throw fail('invalid-number', '未編號樣品不建立編號鎖定');
  let encoded = 'n_';
  for (let index = 0; index < number.length; index += 1) {
    encoded += number.charCodeAt(index).toString(16).padStart(4, '0');
  }
  return encoded;
};

const actorUid = async () => {
  const user = auth.currentUser;
  const role = await getAuthorizedUserRole(user);
  if (!user?.uid || !['admin', 'staff'].includes(role) || auth.currentUser?.uid !== user.uid) {
    throw fail('permission-denied', '請先使用管理員或工作人員帳號登入');
  }
  return user.uid;
};
const sampleQuery = (country, productId) => {
  validCountry(country);
  const conditions = [where('country', '==', country)];
  if (productId !== null && productId !== undefined) {
    validId(productId, '商品');
    conditions.push(where('productId', '==', productId));
  }
  return query(collection(db, 'productSamples'), ...conditions);
};
const toSamples = (snapshot) => snapshot.docs.map((entry) => ({ ...entry.data(), id: entry.id }))
  .sort((left, right) => String(left.number || '').localeCompare(String(right.number || ''), 'zh-Hant') || left.id.localeCompare(right.id));

// productId=null means all pieces in this country; no product or stock document is changed.
export const listProductSamples = async (country, productId = null, { fromServer = false } = {}) => toSamples(await (fromServer ? getDocsFromServer : getDocs)(sampleQuery(country, productId)));
export const subscribeProductSamples = (country, productId, callback, onError) => (
  onSnapshot(sampleQuery(country, productId), (snapshot) => callback(toSamples(snapshot)), onError)
);

export const listSampleProducts = async (country) => {
  validCountry(country);
  const snapshot = await getDocs(collection(db, getCollectionName(country)));
  return snapshot.docs.map((entry) => ({ ...entry.data(), id: entry.id }));
};
export const matchSampleProducts = (products, barcode) => {
  if (typeof barcode !== 'string' || !barcode) return [];
  return products.map((product) => ({
    productId: product.id,
    product,
    variants: getProductVariants(product).filter((variant) => variant.barcode === barcode || variant.rawBarcode === barcode),
  })).filter((match) => match.variants.length > 0 || match.product.barcode === barcode || match.product.rawBarcode === barcode);
};
export const findProductsByExactBarcode = async (country, barcode) => matchSampleProducts(await listSampleProducts(country), barcode);

const claimRef = (number) => doc(db, 'sampleNumberClaims', sampleNumberClaimId(number));
const claimData = (sampleId, sample, uid) => ({
  sampleId, country: sample.country, productId: sample.productId,
  number: sample.number, updatedBy: uid, updatedAt: serverTimestamp(),
});
const checkBatchCount = (count) => {
  if (!Number.isInteger(count) || count < 1 || count > MAX_SAMPLE_BATCH) {
    throw fail('invalid-count', `每批須為 1–${MAX_SAMPLE_BATCH} 件實體樣品`);
  }
};

export const createProductSamples = async ({ country, productId, variantId = '', count = 1, number = '', location = '' }) => {
  validCountry(country);
  validId(productId, '商品');
  if (variantId !== '') validId(variantId, '款式');
  checkBatchCount(count);
  validateSampleNumber(number);
  validateLocation(location);
  if (number && count !== 1) throw fail('duplicate-number', '一個編號只可對應一件實體樣品；多件請先留空再批次編號');
  const uid = await actorUid();
  const refs = Array.from({ length: count }, () => doc(collection(db, 'productSamples')));
  const result = await runTransaction(db, async (transaction) => {
    const productRef = doc(db, getCollectionName(country), productId);
    const product = await transaction.get(productRef);
    const currentProduct = product.exists() ? product.data() : {};
    const variants = getProductVariants(currentProduct);
    if (!product.exists() || (variantId && !variants.some((variant) => variant.id === variantId && variant.active !== false))) {
      throw fail('missing-variant', '商品或款式已變更；請先儲存商品並重新載入後再新增樣品');
    }
    if (number) {
      const claim = await transaction.get(claimRef(number));
      if (claim.exists()) throw fail('duplicate-number', `樣品編號「${number}」已被使用，請換一個編號`);
    }
    // Every read above happens before any write, including on Firestore retries.
    const styleIds = variants.map((variant) => variant.id);
    if (JSON.stringify(currentProduct.styleIds) !== JSON.stringify(styleIds)) {
      // Freeze legacy identities in the same transaction as the first physical reference.
      transaction.update(productRef, { styleIds, revision: (Number(currentProduct.revision) || 0) + 1,
        updatedBy: uid, updatedAt: serverTimestamp() });
    }
    refs.forEach((ref) => {
      const sample = { country, productId, variantId, number, location, revision: 1,
        createdBy: uid, createdAt: serverTimestamp(), updatedBy: uid, updatedAt: serverTimestamp() };
      transaction.set(ref, sample);
      if (number) transaction.set(claimRef(number), claimData(ref.id, sample, uid));
    });
    return refs.map((ref) => ref.id);
  });
  return result;
};

export const updateProductSample = async ({ sampleId, country, productId, expected, changes }) => {
  validCountry(country);
  validId(productId, '商品');
  validId(sampleId, '樣品');
  if (!expected || !Number.isInteger(expected.revision)) throw fail('missing-revision', '請重新載入樣品後重試');
  const fields = Object.keys(changes || {});
  if (!fields.length || fields.some((field) => !['number', 'location'].includes(field))) {
    throw fail('invalid-change', '只能修改樣品編號或位置');
  }
  fields.forEach((field) => field === 'number' ? validateSampleNumber(changes[field]) : validateLocation(changes[field]));
  const uid = await actorUid();
  return runTransaction(db, async (transaction) => {
    const ref = doc(db, 'productSamples', sampleId);
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw fail('missing-sample', '找不到這件樣品');
    const current = snapshot.data();
    if (current.country !== country || current.productId !== productId) throw fail('invalid-sample', '樣品不屬於這個商品');
    const conflictingFields = fields.filter((field) => current.revision !== expected.revision
      && current[field] !== expected[field] && current[field] !== changes[field]);
    if (conflictingFields.length) {
      throw fail('sample-conflict', '其他人已修改同一欄位。你的輸入已保留，請比較最新資料後再儲存', { current: { ...current, id: sampleId }, fields: conflictingFields });
    }
    const updated = { ...current, ...changes };
    const numberChanged = updated.number !== current.number;
    let previousClaim;
    if (numberChanged && current.number) previousClaim = await transaction.get(claimRef(current.number));
    let nextClaim;
    if (numberChanged && updated.number) nextClaim = await transaction.get(claimRef(updated.number));
    if (previousClaim?.exists() && previousClaim.data().sampleId !== sampleId) {
      throw fail('claim-conflict', '原編號的鎖定資料不一致，請聯絡管理員；未儲存任何變更');
    }
    if (nextClaim?.exists() && nextClaim.data().sampleId !== sampleId) {
      throw fail('duplicate-number', `樣品編號「${updated.number}」已被使用，請換一個編號`);
    }
    if (fields.every((field) => current[field] === changes[field])) return { ...current, id: sampleId };
    const revision = current.revision + 1;
    transaction.update(ref, { ...changes, revision, updatedBy: uid, updatedAt: serverTimestamp() });
    if (numberChanged && previousClaim?.exists()) transaction.delete(claimRef(current.number));
    if (numberChanged && updated.number) transaction.set(claimRef(updated.number), claimData(sampleId, updated, uid));
    return { ...updated, revision, id: sampleId };
  });
};

export const buildSampleNumbers = ({ prefix = '', start, width = 4, count }) => {
  checkBatchCount(count);
  if (typeof prefix !== 'string' || !Number.isSafeInteger(start) || start < 0
      || !Number.isInteger(width) || width < 1 || width > 12 || !Number.isSafeInteger(start + count)) {
    throw fail('invalid-allocation', '起號須為非負安全整數，補零寬度須為 1–12');
  }
  return Array.from({ length: count }, (_, offset) => validateSampleNumber(`${prefix}${String(start + offset).padStart(width, '0')}`));
};

export const previewSampleAllocation = async ({ samples, prefix = '', start = 1, width = 4, useCounter = true, location }) => {
  checkBatchCount(samples?.length);
  if (new Set(samples.map((sample) => sample.id)).size !== samples.length) throw fail('duplicate-sample', '不能重複選取同一件樣品');
  samples.forEach((sample) => {
    validCountry(sample.country);
    validId(sample.id, '樣品');
    validId(sample.productId, '商品');
    if (sample.number !== '' || !Number.isInteger(sample.revision)) throw fail('already-numbered', '批次編號只適用於尚未編號的樣品');
  });
  if (location !== undefined) validateLocation(location);
  let counterRevision = null;
  let counterNext = null;
  let actualStart = start;
  if (useCounter) {
    const snapshot = await getDoc(doc(db, 'sampleCounters', 'global'));
    const counter = snapshot.exists() ? snapshot.data() : { nextNumber: 1, revision: 0 };
    if (!Number.isSafeInteger(counter.nextNumber) || counter.nextNumber < 0 || !Number.isInteger(counter.revision)) {
      throw fail('invalid-counter', '共用編號計數器資料無效，請聯絡管理員');
    }
    counterRevision = counter.revision;
    counterNext = counter.nextNumber;
    actualStart = counter.nextNumber;
  }
  const numbers = buildSampleNumbers({ prefix, start: actualStart, width, count: samples.length });
  return {
    useCounter, counterRevision, counterNext, start: actualStart, prefix, width,
    assignments: samples.map((sample, index) => ({
      sampleId: sample.id, country: sample.country, productId: sample.productId,
      variantId: sample.variantId, expectedRevision: sample.revision, expectedNumber: sample.number,
      expectedLocation: sample.location, number: numbers[index],
      location: location === undefined ? sample.location : location,
    })),
  };
};

// Commits exactly the preview. It never silently chooses a different range after a conflict.
export const commitSampleAllocation = async (preview) => {
  checkBatchCount(preview?.assignments?.length);
  const numbers = buildSampleNumbers({ prefix: preview.prefix, start: preview.start, width: preview.width, count: preview.assignments.length });
  if (new Set(preview.assignments.map((entry) => entry.sampleId)).size !== preview.assignments.length
      || preview.assignments.some((entry, index) => entry.number !== numbers[index] || entry.expectedNumber !== '')) {
    throw fail('invalid-preview', '預覽資料無效，請重新預覽');
  }
  preview.assignments.forEach((entry) => {
    validCountry(entry.country);
    validId(entry.sampleId, '樣品');
    validateLocation(entry.location);
  });
  const uid = await actorUid();
  return runTransaction(db, async (transaction) => {
    const counterRef = doc(db, 'sampleCounters', 'global');
    let counter;
    if (preview.useCounter) {
      const snapshot = await transaction.get(counterRef);
      counter = snapshot.exists() ? snapshot.data() : { nextNumber: 1, revision: 0 };
      if (counter.revision !== preview.counterRevision || counter.nextNumber !== preview.counterNext
          || preview.start !== counter.nextNumber) {
        throw fail('counter-conflict', '共用起號已被其他人使用。未儲存任何變更，請重新預覽');
      }
    }
    const snapshots = await Promise.all(preview.assignments.map((entry) => transaction.get(doc(db, 'productSamples', entry.sampleId))));
    const claims = await Promise.all(preview.assignments.map((entry) => transaction.get(claimRef(entry.number))));
    preview.assignments.forEach((entry, index) => {
      const snapshot = snapshots[index];
      const current = snapshot.exists() ? snapshot.data() : null;
      if (!current || current.country !== entry.country || current.productId !== entry.productId
          || current.variantId !== entry.variantId || current.revision !== entry.expectedRevision
          || current.number !== '' || current.location !== entry.expectedLocation) {
        throw fail('sample-conflict', '選取的樣品在預覽後已變更。未儲存任何變更，請重新預覽');
      }
      if (claims[index].exists()) throw fail('duplicate-number', `預覽編號「${entry.number}」已被使用。未儲存任何變更，請調整前綴或起號`);
    });
    // Firestore requires all reads to finish before the first write.
    preview.assignments.forEach((entry, index) => {
      transaction.update(doc(db, 'productSamples', entry.sampleId), {
        number: entry.number, location: entry.location, revision: snapshots[index].data().revision + 1,
        updatedBy: uid, updatedAt: serverTimestamp(),
      });
      transaction.set(claimRef(entry.number), claimData(entry.sampleId, entry, uid));
    });
    if (preview.useCounter) transaction.set(counterRef, {
      nextNumber: preview.start + preview.assignments.length, revision: counter.revision + 1,
      updatedBy: uid, updatedAt: serverTimestamp(),
    });
    return preview.assignments.map((entry) => ({ sampleId: entry.sampleId, number: entry.number }));
  });
};

export const sampleErrorMessage = (error) => error?.code === 'permission-denied'
  ? '沒有樣品資料存取權限，請確認登入身份及 Firestore 規則'
  : error?.code === 'unavailable' ? '目前無法連線。輸入已保留，請連線後重試' : error?.message || '操作失敗，請重試';
